import { useCallback, useRef } from 'react';

const PROXIED_SYMBOL = Symbol('proxied');

type Prop<T, K extends keyof T> = {
  prop: K;
  getter?: (value: T[K], prop: K) => unknown;
  setter?: (value: unknown, prop: K) => T[K];
};

export type PropListItem<T> = { [K in keyof T]-?: Prop<T, K> }[keyof T];

/**
 * Helper function to traverse prototypes and look for a given property descriptor
 * (prioritizes farthest ancestors' descriptors)
 */
function findDescriptorRecursively(
  node: unknown,
  prop: PropertyKey
): PropertyDescriptor | undefined {
  const proto = Object.getPrototypeOf(node);
  if (proto == null) return Object.getOwnPropertyDescriptor(node, prop);
  return (
    findDescriptorRecursively(proto, prop) ??
    Object.getOwnPropertyDescriptor(node, prop)
  );
}

/**
 * React Hook Form bypasses React when setting default values, hence the need
 * for this workaround.
 *
 * It also doesn't emit DOM change events when doing so, so directly listening
 * to DOM events won't help either.
 *
 * So, basically, what we're doing instead is overriding and and proxying the getters and setters
 * of the properties we need to track, so we can detect all programatic changes to their values,
 * even if no events are ever emitted.
 *
 * This doesn't remove the need for regular event listeners, as this method can't detect
 * when the DOM itself updates values (for example, when the user clicks or types something).
 *
 * @param forwardedRef The forwarded ref that will go to RHF
 * @param props List containing the props to be proxied.
 *
 * PS: updates to this list won't be detected, but changes to the getters and setters will.
 *
 * Put simply: don't add/remove/reorder elements, but feel free to update their getters and
 * setters themselves if you need to. Also, no need to use useMemo for the list, getters or setters.
 */
export function useForwardedRefProxy<T extends HTMLElement>(
  forwardedRef: React.ForwardedRef<T>,
  props: readonly PropListItem<T>[]
) {
  const propsRef = useRef(props);
  propsRef.current = props;

  const ref = useRef<T | undefined>(undefined);

  const callbackRef = useCallback(
    (node: T) => {
      if (node && !(PROXIED_SYMBOL in node)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any)[PROXIED_SYMBOL] = true;

        const _props = propsRef.current;
        for (let i = 0; i < _props.length; i++) {
          const prop = _props[i];

          const descriptor = findDescriptorRecursively(node, prop.prop);

          const _getter = descriptor?.get?.bind(node);
          const _setter = descriptor?.set?.bind(node);
          const _value =
            typeof descriptor?.value === 'function'
              ? descriptor.value.bind(node)
              : descriptor?.value;

          const getter = () => {
            const v = _getter ? _getter() : _value;
            const prop = propsRef.current[i];
            const getter = prop?.getter;
            return getter ? getter(v, prop.prop) : v;
          };

          const setter = (v: unknown) => {
            const prop = propsRef.current[i];
            const setter = prop?.setter;
            const r = setter ? setter(v, prop.prop) : v;
            return _setter ? _setter(r) : undefined;
          };

          Object.defineProperty(node, prop.prop, {
            get: getter,
            set: setter,
          });
        }
      }

      ref.current = node;

      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else {
          forwardedRef.current = node;
        }
      }
    },
    [forwardedRef]
  );

  return {
    /**
     * This should be passed to the element's `ref` attribute
     */
    callbackRef,

    /**
     * You can use this to access forwardedRef's value within the component itself
     */
    ref,
  };
}
